import React, { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import EthicsEmblem from "./EthicsEmblem";

const NAV_LINKS = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/register", label: "Register", testid: "nav-register" },
  { to: "/platform", label: "Cockpit VNG Mienskip", testid: "nav-cockpit" },
  { to: "/publiek-overzicht", label: "Publiek overzicht", testid: "nav-public" },
  { to: "/kaders", label: "Kaders", testid: "nav-kaders" },
  { to: "/team", label: "Team", testid: "nav-team" },
];

const Navigation = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const close = () => setOpen(false);
  // Editorial landing page: hide all nav actions; only the brand mark
  // stays so visitors can still get back home from any other route.
  const isLanding = pathname === "/";

  return (
    <header
      data-testid="site-navigation"
      data-landing={isLanding ? "true" : "false"}
      className="sticky top-0 z-40 backdrop-blur-xl border-b border-[#C0C5CE]/15"
      style={{ background: "rgba(28, 58, 90, 0.85)" }}
    >
      <div className="rk-container flex items-center justify-between py-4">
        {/* Brand mark + desktop nav — grouped on the left */}
        <div className="flex items-center gap-10">
          <Link
            to="/"
            aria-label="RiskaiΛ — Home"
            data-testid="nav-brand"
            className="shrink-0 inline-flex items-center"
          >
            <EthicsEmblem width={150} />
          </Link>
          {!isLanding && (
            <nav
              data-testid="nav-links"
              className="hidden lg:flex items-center gap-10"
            >
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={l.testid}
                  className={({ isActive }) =>
                    `relative inline-flex items-center text-[12px] tracking-[0.18em] uppercase transition-colors ${
                      isActive ? "text-white" : "text-[#D4D8DF] hover:text-white"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {l.label}
                      <span
                        className={`absolute -bottom-2 left-0 h-px bg-[#5BC0EB] transition-all duration-500 ${
                          isActive ? "w-full" : "w-0"
                        }`}
                      />
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
          )}
        </div>

        {/* Spacer for mobile so the toggle stays on the right */}
        <span className="lg:hidden" aria-hidden />

        {/* CTA → assessment-choice (gate to cockpit) — hidden on landing */}
        {!isLanding && (
          <Link
            to="/assessment-choice"
            data-testid="nav-cta-assessment"
            className="!hidden lg:!inline-flex rk-btn-cyan"
          >
            Start assessment
            <ArrowUpRight strokeWidth={1.25} className="w-4 h-4" />
          </Link>
        )}

        {/* Mobile menu toggle — hidden on landing */}
        {!isLanding && (
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-[#D4D8DF]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X strokeWidth={1} /> : <Menu strokeWidth={1} />}
          </button>
        )}
      </div>

      {/* Mobile panel */}
      {open && !isLanding && (
        <div
          data-testid="nav-mobile-panel"
          className="lg:hidden border-t border-[#C0C5CE]/15"
        >
          <div className="rk-container py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.testid}-mobile`}
                onClick={close}
                className={`inline-flex items-center text-sm tracking-[0.2em] uppercase ${
                  pathname === l.to ? "text-white" : "text-[#D4D8DF]"
                }`}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/assessment-choice"
              onClick={close}
              data-testid="nav-cta-assessment-mobile"
              className="rk-btn-cyan w-fit"
            >
              Start assessment
              <ArrowUpRight strokeWidth={1.25} className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;
